import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Snackbar,
  Alert as MuiAlert,
  CardActions,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Assessment as AssessmentIcon,
  PauseCircle as PauseIcon,
  BugReport as TestIcon,
  ContentCopy as DuplicateIcon
} from '@mui/icons-material';
import axios from 'axios';
import SurveyStats from '../../components/SurveyStats';

// Функция перевода категорий
const translateCategory = (category) => {
  const translations = {
    'online-surveys': 'Онлайн-опросы',
    'online-interviews': 'Онлайн-интервью и фокус-группы',
    'website-app-testing': 'Тест сайтов и приложений',
    'store-visits': 'Визиты в магазины и офисы',
    'product-testing': 'Тестирование продуктов и услуг',
    'other': 'Другое',
    // Старые категории для обратной совместимости
    general: 'Онлайн-опросы',
    market: 'Онлайн-опросы',
    'market-research': 'Онлайн-опросы',
  };
  return translations[category] || category;
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState({
    totalSurveys: 0,
    activeSurveys: 0,
    totalResponses: 0,
    draftSurveys: 0
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [responsesDialogOpen, setResponsesDialogOpen] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [newSurvey, setNewSurvey] = useState({
    title: '',
    description: '',
    category: '',
    estimatedTime: 5,
    reward: { type: 'money', amount: 1 },
    maxResponses: 100
  });
  
  // Copy link snackbar
  const [linkCopied, setLinkCopied] = useState(false);

  // Duplicate survey
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [duplicateError, setDuplicateError] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

  // Request all statuses (including drafts, pending moderation, rejected, completed, active)
  const surveysRes = await axios.get(`${API_BASE}/surveys?allStatuses=true`, config);
      
      // Handle both old and new response formats
      const surveysData = surveysRes.data.surveys || surveysRes.data;
      setSurveys(Array.isArray(surveysData) ? surveysData : []);

      const surveysArray = Array.isArray(surveysData) ? surveysData : [];
      setStats({
        totalSurveys: surveysArray.length,
        activeSurveys: surveysArray.filter(s => s.status === 'active').length,
        totalResponses: surveysArray.reduce((sum, s) => sum + (s.responseCount || 0), 0),
        draftSurveys: surveysArray.filter(s => s.status === 'draft').length
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleCreateSurvey = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/surveys`, newSurvey, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreateDialogOpen(false);
      setNewSurvey({
        title: '',
        description: '',
        category: '',
        estimatedTime: 5,
        reward: { type: 'money', amount: 1 },
        maxResponses: 100
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error creating survey:', error);
    }
  };

  const handlePublishSurvey = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/surveys/${surveyId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error publishing survey:', error);
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот опрос?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting survey:', error);
      alert('Не удалось удалить опрос');
    }
  };

  const handleDuplicateSurvey = async (survey) => {
    if (duplicatingId) return;
    setDuplicatingId(survey.id);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_BASE}/surveys/${survey.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!data || !data.id) throw new Error('No id in duplicate response');
      navigate(`/client/survey-builder/${data.id}`, {
        state: { duplicatedFrom: survey.id, duplicatedTitle: survey.title }
      });
    } catch (error) {
      console.error('Error duplicating survey:', error);
      setDuplicateError(error.response?.data?.error || 'Не удалось дублировать опрос. Попробуйте ещё раз.');
      setDuplicatingId(null);
    }
  };

  const handleViewResponses = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      const [surveyRes, responsesRes] = await Promise.all([
        axios.get(`${API_BASE}/surveys/${surveyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/surveys/${surveyId}/responses`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSelectedSurvey(surveyRes.data);
      setSurveyResponses(responsesRes.data);
      setResponsesDialogOpen(true);
    } catch (error) {
      console.error('Error fetching responses:', error);
      alert('Не удалось загрузить ответы');
    }
  };

  // Ensure surveys is always an array before filtering
  const surveysArray = Array.isArray(surveys) ? surveys : [];
  const activeSurveys = surveysArray.filter(s => s.status === 'active');
  const draftSurveys = surveysArray.filter(s => s.status === 'draft');
  const pendingSurveys = surveysArray.filter(s => s.status === 'pending_moderation');
  const rejectedSurveys = surveysArray.filter(s => s.status === 'rejected');
  const completedSurveys = surveysArray.filter(s => s.status === 'completed');

  const getDisplayedSurveys = () => {
    switch (currentTab) {
      case 1: return activeSurveys;
      case 2: return draftSurveys;
      case 3: return pendingSurveys;
      case 4: return rejectedSurveys;
      case 5: return completedSurveys;
      default: return surveysArray;
    }
  };

  const displayedSurveys = getDisplayedSurveys();

  const getStatusLabel = (status) => (
    status === 'active' ? 'Активен' :
    status === 'draft' ? 'Черновик' :
    status === 'pending_moderation' ? 'На модерации' :
    status === 'rejected' ? 'Отклонён' :
    status === 'completed' ? 'Завершён' :
    status
  );

  const getStatusChipSx = (status) => ({
    background:
      status === 'active' ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' :
      status === 'draft' ? 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' :
      status === 'pending_moderation' ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' :
      status === 'rejected' ? 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)' :
      'linear-gradient(135deg, #b2bec3 0%, #636e72 100%)',
    color: status === 'draft' ? '#2d3748' : '#fff',
    fontWeight: 600,
    border: 'none',
  });

  const renderSurveyActions = (survey) => (
    <>
      {survey.isPublicLink && survey.status === 'active' && (
        <IconButton
          size="small"
          onClick={() => {
            const publicUrl = `${window.location.origin}/public/survey/${survey.customSlug || survey.id}`;
            navigator.clipboard.writeText(publicUrl);
            setLinkCopied(true);
          }}
          title="Скопировать публичную ссылку"
          sx={{
            color: '#0984e3',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.2) 0%, rgba(9, 132, 227, 0.2) 100%)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <LinkIcon />
        </IconButton>
      )}
      <IconButton
        size="small"
        onClick={() => navigate(`/client/survey/${survey.id}/preview`)}
        title="Просмотр"
        sx={{
          color: '#0984e3',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.2) 0%, rgba(9, 132, 227, 0.2) 100%)',
            transform: 'scale(1.1)'
          },
          transition: 'all 0.3s ease'
        }}
      >
        <VisibilityIcon />
      </IconButton>
      {survey.questions && survey.questions.length > 0 && (
        <IconButton
          size="small"
          onClick={() => navigate(`/client/survey/${survey.id}/test`)}
          title="Тестовое прохождение"
          sx={{
            color: '#ff9800',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.3) 100%)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <TestIcon />
        </IconButton>
      )}
      {survey.status === 'draft' && (
        <IconButton
          size="small"
          onClick={() => handlePublishSurvey(survey.id)}
          title="Опубликовать"
          sx={{
            color: '#2d7a1f',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(86, 171, 47, 0.2) 0%, rgba(45, 122, 31, 0.2) 100%)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <PublishIcon />
        </IconButton>
      )}
      <Tooltip title="Дублировать">
        <span>
          <IconButton
            size="small"
            onClick={() => handleDuplicateSurvey(survey)}
            disabled={!!duplicatingId}
            aria-label="Дублировать"
            sx={{
              color: '#00b894',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(85, 239, 196, 0.2) 0%, rgba(0, 184, 148, 0.2) 100%)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <DuplicateIcon />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton
        size="small"
        onClick={() => handleViewResponses(survey.id)}
        title="Просмотр ответов"
        sx={{
          color: '#6c5ce7',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(162, 155, 254, 0.2) 0%, rgba(108, 92, 231, 0.2) 100%)',
            transform: 'scale(1.1)'
          },
          transition: 'all 0.3s ease'
        }}
      >
        <AssessmentIcon />
      </IconButton>
      {(survey.status === 'draft' || survey.status === 'rejected') && (
        <IconButton
          size="small"
          onClick={() => navigate(`/client/survey-builder/${survey.id}`)}
          title="Редактировать"
          sx={{
            color: '#0984e3',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.2) 0%, rgba(9, 132, 227, 0.2) 100%)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <EditIcon />
        </IconButton>
      )}
      {survey.status === 'draft' && (
        <IconButton
          size="small"
          onClick={() => handleDeleteSurvey(survey.id)}
          title="Удалить"
          sx={{
            color: '#d63031',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.2) 0%, rgba(214, 48, 49, 0.2) 100%)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3,
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#2d3748',
            fontSize: { xs: '1.75rem', md: '2.125rem' }
          }}
        >
          Панель заказчика
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/client/survey-builder')}
          sx={{
            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '12px',
            textTransform: 'none',
            px: 3,
            py: 1.5,
            width: { xs: '100%', sm: 'auto' },
            boxShadow: '0 4px 14px rgba(9, 132, 227, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
              boxShadow: '0 6px 20px rgba(9, 132, 227, 0.6)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Создать новый опрос
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(9, 132, 227, 0.3)',
              color: '#fff',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(9, 132, 227, 0.4)'
              }
            }}
          >
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.875rem' }}>Всего опросов</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.totalSurveys}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(86, 171, 47, 0.3)',
              color: '#fff',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(86, 171, 47, 0.4)'
              }
            }}
          >
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.875rem' }}>Активные опросы</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.activeSurveys}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(108, 92, 231, 0.3)',
              color: '#fff',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(108, 92, 231, 0.4)'
              }
            }}
          >
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.875rem' }}>Всего ответов</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.totalResponses}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(253, 203, 110, 0.3)',
              color: '#2d3748',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(253, 203, 110, 0.4)'
              }
            }}
          >
            <CardContent>
              <Typography sx={{ color: '#2d3748', fontWeight: 600, fontSize: '0.875rem' }}>Черновики</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.draftSurveys}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper 
        sx={{ 
          mb: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '24px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(e, val) => setCurrentTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#718096',
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                color: '#0984e3',
                fontWeight: 700
              },
              '&:hover': {
                color: '#0984e3',
                backgroundColor: 'rgba(116, 185, 255, 0.1)'
              }
            },
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(90deg, #74b9ff 0%, #0984e3 100%)',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label="Все опросы" />
          <Tab label="Активные" />
          <Tab label="Черновики" />
          <Tab label="На модерации" />
          <Tab label="Отклоненные" />
          <Tab label="Завершенные" />
        </Tabs>
      </Paper>

      {/* Surveys - mobile cards */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2, mb: 2 }}>
        {displayedSurveys.map((survey) => (
          <Card
            key={survey.id}
            sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                {survey.title}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Chip label={getStatusLabel(survey.status)} size="small" sx={getStatusChipSx(survey.status)} />
                {survey.isPublicLink && (
                  <Chip label="Публичная ссылка" size="small" sx={{ background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)', color: '#fff', fontWeight: 600 }} />
                )}
                {survey.isExternalSurvey && (
                  <Chip label="Внешний опрос" size="small" sx={{ background: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)', color: '#fff', fontWeight: 600 }} />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Категория: {translateCategory(survey.category) || 'Не указана'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Вопросы: {survey.questions?.length || 0} · Ответы: {survey.responseCount || 0} / {survey.maxResponses || '∞'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Вознаграждение: {survey.isPublicLink ? '—' : `${survey.reward?.amount || 0} BYN`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Создан: {new Date(survey.createdAt).toLocaleDateString('ru-RU')}
              </Typography>
              {survey.status === 'rejected' && survey.rejectionReason && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  Причина отклонения: {survey.rejectionReason}
                </Typography>
              )}
            </CardContent>
            <CardActions sx={{ flexWrap: 'wrap', px: 2, pb: 2, pt: 0 }}>
              {renderSurveyActions(survey)}
            </CardActions>
          </Card>
        ))}
        {displayedSurveys.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '16px' }}>
            <Typography color="textSecondary">Опросы не найдены</Typography>
          </Paper>
        )}
      </Box>

      {/* Surveys Table - desktop */}
      <TableContainer 
        component={Paper}
        sx={{
          display: { xs: 'none', md: 'block' },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '24px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                '& th': {
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderBottom: 'none',
                  py: 2
                }
              }}
            >
              <TableCell sx={{ maxWidth: '200px' }}>Название</TableCell>
              <TableCell sx={{ width: '120px' }}>Категория</TableCell>
              <TableCell sx={{ width: '100px' }}>Статус</TableCell>
              <TableCell sx={{ width: '80px' }}>Вопросы</TableCell>
              <TableCell sx={{ width: '80px' }}>Ответы</TableCell>
              <TableCell sx={{ width: '60px' }}>Вознаграждение</TableCell>
              <TableCell sx={{ width: '90px' }}>Создан</TableCell>
              <TableCell sx={{ width: '100px' }}>Опубликован</TableCell>
              <TableCell align="right" sx={{ width: '180px', pr: 2 }}>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(currentTab === 0 ? surveys : 
              currentTab === 1 ? activeSurveys : 
              currentTab === 2 ? draftSurveys :
              currentTab === 3 ? pendingSurveys :
              currentTab === 4 ? rejectedSurveys :
              completedSurveys).map((survey) => (
              <TableRow 
                key={survey.id}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(116, 185, 255, 0.08)',
                    transition: 'background-color 0.3s ease'
                  },
                  '& td': {
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    py: 2
                  }
                }}
              >
                <TableCell>
                  {survey.title}
                  {survey.isPublicLink && (
                    <Chip 
                      label="Публичная ссылка" 
                      size="small" 
                      sx={{ 
                        ml: 1,
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        border: 'none'
                      }}
                    />
                  )}
                  {survey.isExternalSurvey && (
                    <Chip 
                      label="Внешний опрос" 
                      size="small" 
                      sx={{ 
                        ml: 1,
                        background: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        border: 'none'
                      }}
                    />
                  )}
                  {survey.status === 'rejected' && survey.rejectionReason && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                      Причина отклонения: {survey.rejectionReason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{translateCategory(survey.category) || 'Не указана'}</TableCell>
                <TableCell>
                  <Chip 
                    label={getStatusLabel(survey.status)}
                    size="small" 
                    sx={getStatusChipSx(survey.status)}
                  />
                </TableCell>
                <TableCell>{survey.questions?.length || 0}</TableCell>
                <TableCell>{survey.responseCount || 0} / {survey.maxResponses || '∞'}</TableCell>
                <TableCell>
                  {survey.isPublicLink ? '—' : `${survey.reward?.amount || 0} BYN`}
                </TableCell>
                <TableCell>{new Date(survey.createdAt).toLocaleDateString('ru-RU')}</TableCell>
                <TableCell>
                  {survey.publishedAt ? (
                    <Box>
                      <Typography variant="body2">
                        {new Date(survey.publishedAt).toLocaleDateString('ru-RU')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(survey.publishedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>
                  {renderSurveyActions(survey)}
                </TableCell>
              </TableRow>
            ))}
            {(currentTab === 0 ? surveys : 
              currentTab === 1 ? activeSurveys : 
              currentTab === 2 ? draftSurveys :
              currentTab === 3 ? pendingSurveys :
              currentTab === 4 ? rejectedSurveys :
              completedSurveys).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">Опросы не найдены</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Survey Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>
          Создать новый опрос
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Название опроса"
              fullWidth
              value={newSurvey.title}
              onChange={(e) => setNewSurvey({ ...newSurvey, title: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#0984e3'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: '2px'
                  }
                }
              }}
            />
            <TextField
              label="Описание"
              fullWidth
              multiline
              rows={3}
              value={newSurvey.description}
              onChange={(e) => setNewSurvey({ ...newSurvey, description: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#0984e3'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: '2px'
                  }
                }
              }}
            />
            <TextField
              label="Категория"
              fullWidth
              select
              value={newSurvey.category}
              onChange={(e) => setNewSurvey({ ...newSurvey, category: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#0984e3'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: '2px'
                  }
                }
              }}
            >
              <MenuItem value="market-research">Маркетинговое исследование</MenuItem>
              <MenuItem value="customer-feedback">Отзывы клиентов</MenuItem>
              <MenuItem value="product-testing">Тестирование продукта</MenuItem>
              <MenuItem value="academic">Академическое исследование</MenuItem>
              <MenuItem value="other">Другое</MenuItem>
            </TextField>
            <TextField
              label="Предполагаемое время (минут)"
              type="number"
              fullWidth
              value={newSurvey.estimatedTime}
              onChange={(e) => setNewSurvey({ ...newSurvey, estimatedTime: parseInt(e.target.value) })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#0984e3'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: '2px'
                  }
                }
              }}
            />
            <TextField
              label="Вознаграждение (BYN)"
              type="number"
              fullWidth
              value={newSurvey.reward.amount}
              onChange={(e) => setNewSurvey({ 
                ...newSurvey, 
                reward: { ...newSurvey.reward, amount: parseFloat(e.target.value) }
              })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#0984e3'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: '2px'
                  }
                }
              }}
            />
            <TextField
              label="Максимум ответов"
              type="number"
              fullWidth
              value={newSurvey.maxResponses}
              onChange={(e) => setNewSurvey({ ...newSurvey, maxResponses: parseInt(e.target.value) })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#0984e3'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: '2px'
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{
              color: '#718096',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(113, 128, 150, 0.1)'
              }
            }}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleCreateSurvey} 
            variant="contained"
            disabled={!newSurvey.title || !newSurvey.category}
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              boxShadow: '0 4px 14px rgba(9, 132, 227, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                boxShadow: '0 6px 20px rgba(9, 132, 227, 0.6)'
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #b2bec3 0%, #636e72 100%)',
                color: '#fff',
                opacity: 0.6
              }
            }}
          >
            Создать опрос
          </Button>
        </DialogActions>
      </Dialog>

      {/* Responses Dialog with Statistics */}
      <Dialog 
        open={responsesDialogOpen} 
        onClose={() => setResponsesDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100vh - 16px)', sm: '90vh' },
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: { xs: '1.1rem', md: '1.5rem' }, pr: 6 }}>
          Статистика и ответы: {selectedSurvey?.title}
        </DialogTitle>
        <DialogContent>
          {selectedSurvey && surveyResponses.length > 0 ? (
            <SurveyStats survey={selectedSurvey} responses={surveyResponses} hideRespondentIdentity />
          ) : (
            <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
              Пока нет ответов на этот опрос
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setResponsesDialogOpen(false)}
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              boxShadow: '0 4px 14px rgba(9, 132, 227, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                boxShadow: '0 6px 20px rgba(9, 132, 227, 0.6)'
              }
            }}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for link copied */}
      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert onClose={() => setLinkCopied(false)} severity="success" sx={{ width: '100%' }}>
          Ссылка скопирована в буфер обмена!
        </MuiAlert>
      </Snackbar>

      {/* Snackbar for duplicate errors */}
      <Snackbar
        open={!!duplicateError}
        autoHideDuration={5000}
        onClose={() => setDuplicateError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert onClose={() => setDuplicateError('')} severity="error" sx={{ width: '100%' }}>
          {duplicateError}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
}
